/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationRemoteSessionIssuersMigrate } from "../funcs/organizationRemoteSessionIssuersMigrate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { MigrateOrganizationRemoteSessionIssuerResult } from "../models/components/migrateorganizationremotesessionissuerresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  MigrateOrganizationRemoteSessionIssuerRequest,
  MigrateOrganizationRemoteSessionIssuerSecurity,
} from "../models/operations/migrateorganizationremotesessionissuer.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type MigrateOrganizationRemoteSessionIssuerMutationVariables = {
  request: MigrateOrganizationRemoteSessionIssuerRequest;
  security?: MigrateOrganizationRemoteSessionIssuerSecurity | undefined;
  options?: RequestOptions;
};

export type MigrateOrganizationRemoteSessionIssuerMutationData =
  MigrateOrganizationRemoteSessionIssuerResult;

export type MigrateOrganizationRemoteSessionIssuerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * migrateIssuer organizationRemoteSessionIssuers
 *
 * @remarks
 * Consolidate two remote_session_issuers that point at the same upstream authorization server: re-point every client from the source issuer onto the target issuer, then soft-delete the source. Existing remote sessions are preserved, so no user re-authenticates. Both issuers must belong to the caller's organization and agree on issuer, token_endpoint, and authorization_endpoint. The issuer identifier is compared canonically, so two spellings differing only by a trailing slash or an explicit default port count as the same upstream; the two endpoints are compared literally. The target may not be narrower in scope than the source: a project-specific issuer may migrate onto an issuer in the same project or onto an organization-level issuer, and an organization-level issuer may migrate onto another organization-level issuer. Requires org:admin.
 */
export function useMigrateOrganizationRemoteSessionIssuerMutation(
  options?: MutationHookOptions<
    MigrateOrganizationRemoteSessionIssuerMutationData,
    MigrateOrganizationRemoteSessionIssuerMutationError,
    MigrateOrganizationRemoteSessionIssuerMutationVariables
  >,
): UseMutationResult<
  MigrateOrganizationRemoteSessionIssuerMutationData,
  MigrateOrganizationRemoteSessionIssuerMutationError,
  MigrateOrganizationRemoteSessionIssuerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildMigrateOrganizationRemoteSessionIssuerMutation(client, options),
    ...options,
  });
}

export function mutationKeyMigrateOrganizationRemoteSessionIssuer(): MutationKey {
  return ["@gram/client", "organizationRemoteSessionIssuers", "migrate"];
}

export function buildMigrateOrganizationRemoteSessionIssuerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: MigrateOrganizationRemoteSessionIssuerMutationVariables,
  ) => Promise<MigrateOrganizationRemoteSessionIssuerMutationData>;
} {
  return {
    mutationKey: mutationKeyMigrateOrganizationRemoteSessionIssuer(),
    mutationFn: function migrateOrganizationRemoteSessionIssuerMutationFn({
      request,
      security,
      options,
    }): Promise<MigrateOrganizationRemoteSessionIssuerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(organizationRemoteSessionIssuersMigrate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
